import React from "react";
import {
  Formik,
  FormikActions,
  Form as FormikForm,
  Field,
  FormikProps,
} from "formik";
import { InjectedIntlProps, injectIntl, FormattedMessage } from "react-intl";
import TextInput from "../../text-input";
import PasswordInput from "../../password-input";
import Typography from "../../typography";
import InputErrorMessage from "../../input-error-message";

import messages from "./RegisterForm.intl";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../../types/styled-arguments";
import RegisterStepButton from "../register-step-button";
import {
  required,
  validPassword,
  FormPasswordInstructions,
} from "../../form-utils";
import { Card, Box } from "@material-ui/core";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";

const Title = styled(({ children }) => (
  <Typography.H5>{children}</Typography.H5>
))((props: IStyledArguments) => {
  const { md } = props.theme.breakpoints.values;

  return {
    color: props.theme.palette.primary.main,
    fontWeight: 700,
    marginBottom: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    [`@media (max-width: ${md}px)`]: {
      fontSize: "1.25rem",
      marginBottom: 20,
    },
  };
});

const FormSection = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: "16px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    borderRadius: "12px",
    marginBottom: theme.spacing(2),
  },
}));

const SectionTitle = styled(Typography.Subtitle1)(({ theme }: IStyledArguments) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const LogoContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  "& img": {
    maxWidth: "240px",
    height: "auto",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "180px",
    },
  },
}));

const WelcomeText = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  marginBottom: theme.spacing(3),
  "& p": {
    color: theme.palette.text.secondary,
    margin: theme.spacing(0.5, 0),
    fontSize: "0.95rem",
  },
  "& a": {
    color: theme.palette.primary.main,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

// const RoleSelect = injectIntl<SelectProps>(({ onChange, value, intl }) => {
//   const positions: { [index: string]: string } = {
//     COACH: "Coach",
//     CLUB: "Club",
//     PLAYER: "PLAYER",
//     CENTREBACK: "Centre Back",
//     CENTREMIDFIELD: "Centre Midfield",
//     LEFTMIDFIELD: "Left Midfield",
//     RIGHTMIDFIELD: "Right Midfield",
//     STRIKER: "Striker",
//   };

//   return (
//     <Select
//       id="position-select"
//       label={intl.formatMessage(messages.role)}
//       value={value}
//       onChange={onChange}
//       fullWidth
//     >
//       {Object.keys(positions).map((key, idx) => (
//         <Option key={idx} value={key}>
//           {positions[key]}
//         </Option>
//       ))}
//     </Select>
//   );
// });

export interface IFormValues {
  name: string;
  surname: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  newUser: boolean;
  // role: string;
}

interface IForm extends InjectedIntlProps {
  onSubmit: (arg: IFormValues) => void;
  checkValid: boolean;
  name: string;
  surname: string;
  email: string;
  password?: string;
  passwordConfirmation?: string;
  newUser: boolean;
  registrationDetails: any;
  // role: string;
}

const StyledForm = styled(FormikForm)(() => ({
  display: "flex",
  flexDirection: "column",
}));

const RegisterForm = ({
  onSubmit,
  checkValid,
  // email,
  name,
  surname,
  password,
  passwordConfirmation = "",
  newUser,
  registrationDetails,
  // role,
  intl,
}: IForm) => {
  return (
    <Formik
      initialValues={{
        email: (registrationDetails && registrationDetails.email) || "",
        name,
        surname,
        password: password || "",
        passwordConfirmation,
        newUser: false,
      }}
      isInitialValid={checkValid}
      onSubmit={(values: IFormValues, actions: FormikActions<IFormValues>) => {
        onSubmit(values);
        actions.setSubmitting(false);
      }}
      render={({
        errors,
        touched,
        values,
        isValid,
      }: FormikProps<IFormValues>) => (
        <>
          <LogoContainer>
            <img src="/images/footik-logo.svg" alt="Footik Logo" />
          </LogoContainer>

          <Title>
            <PersonAddIcon style={{ fontSize: "32px" }} />
            <FormattedMessage {...messages.registerTitle} />
          </Title>

          <WelcomeText>
            <p>
              <FormattedMessage {...messages.loginLinkMessage} />
              &nbsp;
              <a data-testid="navigate-to-login" href="/login">
                <FormattedMessage {...messages.login} />
              </a>
            </p>
          </WelcomeText>

          <StyledForm data-testid="register-form">
            <FormSection>
              <SectionTitle>
                <EmailIcon />
                Email Address
              </SectionTitle>
              <Field
                name="email"
                component={TextInput}
                inputProps={{
                  "data-testid": "email",
                }}
                disabled={!newUser}
                label={intl.formatMessage(messages.email)}
              />
            </FormSection>

            <FormSection>
              <SectionTitle>
                <AccountCircleIcon />
                Personal Information
              </SectionTitle>
              <Field
                name="name"
                validate={required(intl)}
                error={errors.name && touched.name}
                component={TextInput}
                inputProps={{
                  "data-testid": "name",
                }}
                label={intl.formatMessage(messages.firstName)}
              />
              <InputErrorMessage name="name" />
              <Field
                name="surname"
                validate={required(intl)}
                error={errors.name && touched.surname}
                component={TextInput}
                inputProps={{
                  "data-testid": "surname",
                }}
                label={intl.formatMessage(messages.lastName)}
              />
              <InputErrorMessage name="surname" />
            </FormSection>

            <FormSection>
              <SectionTitle>
                <LockIcon />
                Security
              </SectionTitle>
              <Field
                name="password"
                validate={(value: string) =>
                  validPassword(intl)(value, values.email)
                }
                component={PasswordInput}
                inputProps={{
                  "data-testid": "password",
                }}
                label={intl.formatMessage(messages.password)}
              />
              <InputErrorMessage name="password" />
              <FormPasswordInstructions />
            </FormSection>
            <div>
              <RegisterStepButton
                type="submit"
                data-testid="go-next-button"
                disabled={!isValid}
              >
                Next
              </RegisterStepButton>
            </div>
          </StyledForm>
        </>
      )}
    />
  );
};

export default injectIntl(RegisterForm);
